import React from 'react';
import { string, bool, oneOfType } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import { NO_ACCESS_PAGE_INITIATE_TRANSACTIONS } from '../../../util/urlHelpers';
import * as validators from '../../../util/validators';
import { propTypes } from '../../../util/types';
import {
  isErrorNoPermissionForInitiateTransactions,
  isErrorNoPermissionForUserPendingApproval,
  isTooManyRequestsError,
} from '../../../util/errors';

import {
  Form,
  PrimaryButton,
  FieldTextInput,
  Heading,
  NamedLink,
  Avatar,
} from '../../../components';
import TermsAndConditions from './TermsAndConditions/TermsAndConditions';

import css from './RequestPriceForm.module.css';

const MESSAGE_MAX_LENGTH = 500;

const ErrorMessage = props => {
  const { error } = props;
  const userPendingApproval = isErrorNoPermissionForUserPendingApproval(error);
  const userHasNoTransactionRights = isErrorNoPermissionForInitiateTransactions(error);

  // No transaction process attached to listing
  return error ? (
    <p className={css.error}>
      {error.message === 'No transaction process attached to listing' ? (
        <FormattedMessage id="RequestPriceForm.requestPriceErrorNoProcess" />
      ) : isTooManyRequestsError(error) ? (
        <FormattedMessage id="RequestPriceForm.tooManyRequestsError" />
      ) : userPendingApproval ? (
        <FormattedMessage id="RequestPriceForm.userPendingApprovalError" />
      ) : userHasNoTransactionRights ? (
        <FormattedMessage
          id="RequestPriceForm.noTransactionRightsError"
          values={{
            NoAccessLink: msg => (
              <NamedLink
                name="NoAccessPage"
                params={{ missingAccessRight: NO_ACCESS_PAGE_INITIATE_TRANSACTIONS }}
              >
                {msg}
              </NamedLink>
            ),
          }}
        />
      ) : (
        <FormattedMessage id="RequestPriceForm.requestPriceError" />
      )}
    </p>
  ) : null;
};

const RequestPriceFormComponent = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        submitButtonWrapperClassName,
        formId,
        handleSubmit,
        inProgress,
        intl,
        invalid,
        values,
        author,
        authorDisplayName,
        requestPriceError,
      } = fieldRenderProps;

      const messageLabel = intl.formatMessage({
        id: 'RequestPriceForm.messageLabel',
      });
      const messagePlaceholder = intl.formatMessage({
        id: 'RequestPriceForm.messagePlaceholder',
      });
      const messageRequiredMessage = intl.formatMessage({
        id: 'RequestPriceForm.messageRequired',
      });
      const messageMaxLengthMessage = intl.formatMessage(
        {
          id: 'RequestPriceForm.messageMaxLength',
        },
        { maxLength: MESSAGE_MAX_LENGTH }
      );
      const messageRequired = validators.requiredAndNonEmptyString(messageRequiredMessage);
      const messageMaxLength = validators.maxLength(messageMaxLengthMessage, MESSAGE_MAX_LENGTH);

      const termsAndConditionsFromValues = values?.termsAndConditions || [];
      const isTermsAndConditionsAccepted = termsAndConditionsFromValues.length > 0;

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = invalid || submitInProgress || !isTermsAndConditionsAccepted;

      return (
        <Form className={classes} onSubmit={handleSubmit} enforcePagePreloadFor="OrderDetailsPage">
          <Heading as="h2" rootClassName={css.heading}>
            <Avatar className={css.avatar} user={author} />
            <FormattedMessage id="RequestPriceForm.heading" values={{ authorDisplayName }} />
          </Heading>
          <FieldTextInput
            className={css.field}
            inputRootClass={css.input}
            type="textarea"
            name="message"
            id={formId ? `${formId}.message` : 'message'}
            label={messageLabel}
            placeholder={messagePlaceholder}
            validate={validators.composeValidators(messageRequired, messageMaxLength)}
          />
          <TermsAndConditions />
          <div className={submitButtonWrapperClassName}>
            <ErrorMessage error={requestPriceError} />
            <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
              <FormattedMessage id="RequestPriceForm.submitButtonText" />
            </PrimaryButton>
          </div>
        </Form>
      );
    }}
  />
);

RequestPriceFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  submitButtonWrapperClassName: null,
  inProgress: false,
  requestPriceError: null,
};

RequestPriceFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  submitButtonWrapperClassName: string,

  inProgress: bool,

  author: oneOfType([propTypes.user, propTypes.currentUser]),
  authorDisplayName: string.isRequired,
  requestPriceError: propTypes.error,

  // from injectIntl
  intl: intlShape.isRequired,
};

const RequestPriceForm = compose(injectIntl)(RequestPriceFormComponent);

RequestPriceForm.displayName = 'RequestPriceForm';

export default RequestPriceForm;
